(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px)');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const addUrl = () => (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js';

  const addToCart = async (items) => {
    const response = await fetch(addUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || (data.status && data.status !== 200)) throw new Error(data.description || data.message || '');
    document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
    if (window.theme && theme.settings && theme.settings.cart_type === 'drawer' && document.querySelector('.cart-drawer')) {
      document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false }));
    }
    return data;
  };

  const flash = (button, text) => {
    const label = button.querySelector('[data-lp-add-label]') || button.querySelector('span');
    const original = label ? label.textContent : '';
    button.classList.add('is-added');
    if (label) label.textContent = text;
    setTimeout(() => { button.classList.remove('is-added'); if (label) label.textContent = original; }, 2200);
  };

  const init = (root = document) => root.querySelectorAll('[data-lp]').forEach(page => {
    if (page.dataset.ready) return;
    page.dataset.ready = 'true';
    const abort = new AbortController();
    const signal = abort.signal;
    const motion = page.dataset.motion !== 'false' && !reduced.matches && !designMode();
    const observers = [];

    /* Keep anchors clear of the sticky site header. */
    const siteHeader = document.querySelector('.section-header');
    const measureHeader = () => {
      if (!siteHeader) return;
      const position = getComputedStyle(siteHeader).position;
      const height = position === 'sticky' || position === 'fixed' ? Math.round(siteHeader.getBoundingClientRect().height) : 0;
      page.style.setProperty('--lp-header', height + 'px');
    };
    measureHeader();
    if (siteHeader && 'ResizeObserver' in window) {
      const headerObserver = new ResizeObserver(measureHeader);
      headerObserver.observe(siteHeader);
      observers.push(headerObserver);
    }

    /* Fit oversized display words (e.g. "Resurrection") to their line without overflow. */
    const fitTargets = [...page.querySelectorAll('[data-lp-fit]')];
    const fit = () => fitTargets.forEach(element => {
      element.style.removeProperty('font-size');
      const available = element.clientWidth;
      const needed = element.scrollWidth;
      if (available > 0 && needed > available + 1) {
        const size = parseFloat(getComputedStyle(element).fontSize);
        element.style.fontSize = Math.floor(size * (available / needed) * 0.98) + 'px';
      }
    });
    if (fitTargets.length) {
      fit();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
      let fitFrame = 0;
      addEventListener('resize', () => { cancelAnimationFrame(fitFrame); fitFrame = requestAnimationFrame(fit); }, { passive: true, signal });
    }

    /* Hero video: poster first, film after the page has loaded. */
    const video = page.querySelector('[data-lp-video]');
    const toggle = page.querySelector('[data-lp-video-toggle]');
    if (video && motion && !saveData()) {
      const start = () => {
        const src = mobile.matches ? video.dataset.mobile : video.dataset.desktop;
        if (!src) return;
        video.src = src;
        video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true, signal });
        const attempt = video.play();
        if (attempt && attempt.catch) attempt.catch(() => {});
        if (toggle) toggle.hidden = false;
      };
      if (document.readyState === 'complete') setTimeout(start, 200);
      else addEventListener('load', () => setTimeout(start, 200), { once: true, signal });
      if (toggle) {
        toggle.addEventListener('click', () => {
          const paused = toggle.getAttribute('aria-pressed') === 'true';
          if (paused) { video.play().catch(() => {}); toggle.setAttribute('aria-pressed', 'false'); toggle.setAttribute('aria-label', toggle.dataset.labelPause || 'Pause'); }
          else { video.pause(); toggle.setAttribute('aria-pressed', 'true'); toggle.setAttribute('aria-label', toggle.dataset.labelPlay || 'Play'); }
        }, { signal });
      }
      if ('IntersectionObserver' in window) {
        const heroObserver = new IntersectionObserver(entries => entries.forEach(entry => {
          if (!video.src || (toggle && toggle.getAttribute('aria-pressed') === 'true')) return;
          if (entry.isIntersecting) video.play().catch(() => {}); else video.pause();
        }), { threshold: 0.05 });
        heroObserver.observe(video);
        observers.push(heroObserver);
      }
    }

    /* Crucifix buy box: flavor switching. */
    const mainForm = page.querySelector('[data-lp-main-buy]');
    const scenes = [...page.querySelectorAll('[data-lp-scene]')];
    const priceEl = page.querySelector('[data-lp-price]');
    const compareEl = page.querySelector('[data-lp-compare]');
    const productLink = page.querySelector('[data-lp-product-link]');
    const sticky = page.querySelector('[data-lp-sticky]');
    const stickyFlavor = page.querySelector('[data-lp-sticky-flavor]');
    const stickyPrice = page.querySelector('[data-lp-sticky-price]');
    const selectedRadio = () => mainForm && mainForm.querySelector('input[name="id"]:checked');
    const syncFlavor = () => {
      const radio = selectedRadio();
      if (!radio) return;
      scenes.forEach(scene => { scene.hidden = scene.dataset.lpScene !== radio.value; });
      if (priceEl) priceEl.textContent = radio.dataset.price;
      if (compareEl) { compareEl.textContent = radio.dataset.compare || ''; compareEl.hidden = !radio.dataset.compare; }
      if (productLink && radio.dataset.url) productLink.href = radio.dataset.url;
      if (stickyFlavor) stickyFlavor.textContent = radio.dataset.title || '';
      if (stickyPrice) stickyPrice.textContent = radio.dataset.price;
    };
    const packQuantity = () => Math.max(1, parseInt(mainForm && new FormData(mainForm).get('quantity'), 10) || 1);
    const eachNote = page.querySelector('[data-lp-each]');
    const packTotals = [...page.querySelectorAll('[data-lp-pack-total]')];
    let formatMoney = cents => '$' + (cents / 100).toFixed(2);
    try {
      const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: (mainForm && mainForm.dataset.currency) || 'USD' });
      formatMoney = cents => money.format(cents / 100);
    } catch (error) {}
    /* The price always shows what the chosen pack costs. */
    const syncPack = () => {
      const radio = selectedRadio();
      const unit = radio ? Number(radio.dataset.cents) || 0 : 0;
      if (!unit) return;
      const compareUnit = Number(radio.dataset.compareCents) || 0;
      const quantity = packQuantity();
      packTotals.forEach(total => { total.textContent = formatMoney(unit * Number(total.dataset.lpPackTotal)); });
      if (priceEl) priceEl.textContent = formatMoney(unit * quantity);
      if (compareEl && compareUnit > unit) { compareEl.textContent = formatMoney(compareUnit * quantity); compareEl.hidden = false; }
      if (stickyPrice) stickyPrice.textContent = formatMoney(unit * quantity);
      if (eachNote) {
        eachNote.hidden = quantity < 2;
        eachNote.textContent = quantity > 1 ? quantity + ' × ' + formatMoney(unit) : '';
      }
    };
    if (mainForm) {
      mainForm.addEventListener('change', event => {
        if (event.target.name === 'id') syncFlavor();
        if (event.target.name === 'id' || event.target.name === 'quantity') syncPack();
      }, { signal });
    }

    /* All add-to-cart forms on the page. */
    page.querySelectorAll('[data-lp-buy]').forEach(form => {
      const price = form.querySelector('[data-lp-price]');
      if (price && form !== mainForm) {
        form.addEventListener('change', event => {
          const target = event.target;
          const source = target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
          if (target.name === 'id' && source && source.dataset.price) price.textContent = source.dataset.price;
        }, { signal });
      }
      form.addEventListener('submit', async event => {
        if (!window.fetch) return;
        event.preventDefault();
        const button = form.querySelector('[data-lp-add]');
        const status = form.querySelector('.lp-status');
        const data = new FormData(form);
        const id = Number(data.get('id'));
        const quantity = Math.max(1, parseInt(data.get('quantity'), 10) || 1);
        if (!id || !button || button.getAttribute('aria-busy') === 'true') return;
        button.setAttribute('aria-busy', 'true');
        if (status) status.textContent = '';
        try {
          await addToCart([{ id, quantity }]);
          flash(button, 'Added');
        } catch (error) {
          if (error.message) { if (status) status.textContent = error.message; }
          else if (form.dataset.productUrl) window.location.href = form.dataset.productUrl + '?variant=' + id;
        } finally {
          button.removeAttribute('aria-busy');
        }
      }, { signal });
    });

    /* Sticky bar: visible after the buy box scrolls away, hidden near the footer. */
    if (sticky && mainForm && 'IntersectionObserver' in window) {
      const stickyButton = sticky.querySelector('[data-lp-sticky-add]');
      let formVisible = true, heroVisible = true, footerVisible = false;
      const hero = page.querySelector('.lp-hero');
      const footer = document.querySelector('.section-footer') || [...document.querySelectorAll('footer')].find(element => !page.contains(element));
      const update = () => {
        const show = !formVisible && !heroVisible && !footerVisible;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyButton) stickyButton.tabIndex = show ? 0 : -1;
      };
      const stickyObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.target === mainForm) formVisible = entry.isIntersecting;
        if (entry.target === hero) heroVisible = entry.isIntersecting;
        if (entry.target === footer) footerVisible = entry.isIntersecting;
        update();
      }), { threshold: 0 });
      stickyObserver.observe(mainForm);
      if (hero) stickyObserver.observe(hero);
      if (footer) stickyObserver.observe(footer);
      observers.push(stickyObserver);
      if (stickyButton) {
        stickyButton.addEventListener('click', async () => {
          const radio = selectedRadio();
          if (!radio || radio.disabled) { mainForm.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' }); return; }
          stickyButton.setAttribute('aria-busy', 'true');
          try { await addToCart([{ id: Number(radio.value), quantity: packQuantity() }]); flash(stickyButton, 'Added'); }
          catch (error) { mainForm.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
          finally { stickyButton.removeAttribute('aria-busy'); }
        }, { signal });
      }
    }

    /* The full ritual: Crucifix + T-shirt + IceShaker in one add. */
    const bundle = page.querySelector('[data-lp-bundle]');
    if (bundle && mainForm) {
      const bundleButton = bundle.querySelector('[data-lp-bundle-add]');
      const bundleTotal = bundle.querySelector('[data-lp-bundle-total]');
      const bundleStatus = bundle.querySelector('[data-lp-bundle-status]');
      const parts = () => [mainForm, ...page.querySelectorAll('[data-lp-bundle-part]')];
      const pick = form => {
        const control = form.querySelector('input[name="id"]:checked') || form.querySelector('select[name="id"]') || form.querySelector('input[type="hidden"][name="id"]');
        if (!control) return null;
        const source = control.tagName === 'SELECT' ? control.selectedOptions[0] : control;
        return { id: Number(control.value), cents: Number(source && source.dataset.cents) || 0, available: !control.disabled };
      };
      let format = cents => String(cents / 100);
      try {
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: bundle.dataset.currency || 'USD' });
        format = cents => money.format(cents / 100);
      } catch (error) {}
      const updateBundle = () => {
        const items = parts().map(pick);
        if (items.some(item => !item) || !bundleTotal) return;
        bundleTotal.textContent = format(items.reduce((sum, item) => sum + item.cents, 0));
      };
      page.addEventListener('change', updateBundle, { signal });
      if (bundleButton) {
        bundleButton.addEventListener('click', async () => {
          const items = parts().map(pick);
          if (items.some(item => !item || !item.available) || bundleButton.getAttribute('aria-busy') === 'true') return;
          bundleButton.setAttribute('aria-busy', 'true');
          if (bundleStatus) bundleStatus.textContent = '';
          try {
            await addToCart(items.map(item => ({ id: item.id, quantity: 1 })));
            flash(bundleButton, 'Added');
          } catch (error) {
            if (bundleStatus) bundleStatus.textContent = error.message || 'Something went wrong. Please try again.';
          } finally {
            bundleButton.removeAttribute('aria-busy');
          }
        }, { signal });
      }
    }

    /* Lee Priest film. */
    const film = page.querySelector('[data-lp-film]');
    const filmPlay = film && film.querySelector('[data-lp-film-play]');
    if (filmPlay) {
      filmPlay.addEventListener('click', () => {
        const player = film.querySelector('.lp-film__player');
        const filmVideo = player && player.querySelector('video');
        if (!player || !filmVideo) return;
        if (!filmVideo.src && filmVideo.dataset.desktop) filmVideo.src = mobile.matches ? (filmVideo.dataset.mobile || filmVideo.dataset.desktop) : filmVideo.dataset.desktop;
        player.hidden = false;
        filmPlay.hidden = true;
        filmVideo.play().catch(() => {});
        filmVideo.focus();
      }, { signal });
    }

    /* Reveal + dose counters. */
    const countUp = element => {
      const target = Number(element.dataset.lpCount);
      if (!Number.isFinite(target) || target <= 0) return;
      const format = value => Math.round(value).toLocaleString('en-US');
      const duration = 1300;
      let startTime = 0;
      const tick = time => {
        startTime = startTime || time;
        const progress = Math.min(1, (time - startTime) / duration);
        element.textContent = format(target * (1 - Math.pow(1 - progress, 4)));
        if (progress < 1) requestAnimationFrame(tick); else element.textContent = format(target);
      };
      requestAnimationFrame(tick);
    };
    const reveals = [...page.querySelectorAll('[data-lp-reveal]')];
    if (motion && 'IntersectionObserver' in window) {
      page.classList.add('is-motion');
      const revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        entry.target.querySelectorAll('[data-lp-count]').forEach(countUp);
        revealObserver.unobserve(entry.target);
      }), { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      reveals.forEach(element => {
        if (element.getBoundingClientRect().top < innerHeight * 0.9) element.classList.add('is-in');
        else revealObserver.observe(element);
      });
      observers.push(revealObserver);
    }

    /* Holy Water countdown (only for a valid future timestamp). */
    const countdown = page.querySelector('[data-lp-countdown]');
    if (countdown) {
      const launch = Date.parse(countdown.dataset.lpCountdown);
      if (Number.isFinite(launch) && launch > Date.now()) {
        const parts = { days: countdown.querySelector('[data-days]'), hours: countdown.querySelector('[data-hours]'), minutes: countdown.querySelector('[data-minutes]'), seconds: countdown.querySelector('[data-seconds]') };
        const pad = value => String(value).padStart(2, '0');
        const render = () => {
          const left = Math.max(0, launch - Date.now());
          if (!left) { countdown.hidden = true; clearInterval(timer); return; }
          parts.days.textContent = pad(Math.floor(left / 864e5));
          parts.hours.textContent = pad(Math.floor(left / 36e5) % 24);
          parts.minutes.textContent = pad(Math.floor(left / 6e4) % 60);
          parts.seconds.textContent = pad(Math.floor(left / 1e3) % 60);
        };
        countdown.hidden = false;
        const timer = setInterval(render, 1000);
        render();
        signal.addEventListener('abort', () => clearInterval(timer));
      }
    }

    syncFlavor();
    syncPack();
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(page)) return;
      abort.abort();
      observers.forEach(observer => observer.disconnect());
    }, { signal });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
